import { useMemo, useState } from "react";
import { Building2, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useOrganizations } from "@/pages/organizations/hooks/useOrganization";

interface OrganizationPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
}

export function OrganizationPicker({ value, onChange }: OrganizationPickerProps) {
  const { data: organizations, isLoading } = useOrganizations();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (organizations ?? []).filter((org) => !q || org.name.toLowerCase().includes(q));
  }, [organizations, search]);

  const selectedNames = (organizations ?? [])
    .filter((org) => value.includes(org._id))
    .map((org) => org.name);

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const label =
    value.length === 0
      ? "Select organizations"
      : selectedNames.length && selectedNames.length <= 2
        ? selectedNames.join(", ")
        : `${value.length} organizations selected`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal">
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{label}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
        <div className="relative border-b p-2">
          <Search className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organizations..."
            className="h-8 pl-8 text-sm"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {isLoading ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">Loading organizations...</p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">No organizations found</p>
          ) : (
            filtered.map((org) => (
              <label
                key={org._id}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  checked={value.includes(org._id)}
                  onCheckedChange={() => toggle(org._id)}
                />
                <span className="truncate">{org.name}</span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
